import { db } from '../../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { AuditService } from './AuditService';

/**
 * HIPAA Psychotherapy Notes — 45 CFR 164.501
 *
 * Psychotherapy notes are the personal notes of a mental health professional
 * documenting or analyzing the contents of a counseling session. They are:
 *   - Kept SEPARATE from the medical record
 *   - NOT shared with the client (unlike progress notes)
 *   - NOT shared with insurance companies
 *   - Require SEPARATE patient authorization for disclosure
 *   - Immutable once signed (cannot be edited after signing)
 *
 * This service uses a dedicated `psychotherapy_notes` Firestore collection,
 * completely separate from `clinical_notes`.
 */

export type PsychNoteStatus = 'draft' | 'signed';

export interface PsychotherapyNote {
  id: string;
  sessionId: string;         // Link to the appointment/session
  patientId: string;
  providerId: string;
  content: string;           // Free-form personal observations
  createdAt: Date;
  lastEditedAt: Date;
  signedBy?: string;
  signedAt?: Date;
  status: PsychNoteStatus;
  linkedClinicalNoteId?: string; // Optional link to the corresponding progress note
}

export class FirebasePsychotherapyNotesService {
  private static COLLECTION = 'psychotherapy_notes';

  /**
   * Get all psychotherapy notes for a specific patient (provider-only).
   */
  static async getNotesByPatient(providerId: string, patientId: string): Promise<PsychotherapyNote[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('providerId', '==', providerId),
        where('patientId', '==', patientId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.fromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching psychotherapy notes:', error);
      throw error;
    }
  }

  /**
   * Get a single psychotherapy note by ID.
   */
  static async getNote(noteId: string): Promise<PsychotherapyNote | null> {
    try {
      const docSnap = await getDoc(doc(db, this.COLLECTION, noteId));
      if (docSnap.exists()) {
        return this.fromFirestore(docSnap.id, docSnap.data());
      }
      return null;
    } catch (error) {
      console.error('Error fetching psychotherapy note:', error);
      return null;
    }
  }

  /**
   * Create a new psychotherapy note.
   */
  static async createNote(
    noteData: Omit<PsychotherapyNote, 'id' | 'createdAt' | 'lastEditedAt'>
  ): Promise<string> {
    try {
      const docRef = doc(collection(db, this.COLLECTION));
      await setDoc(docRef, {
        ...noteData,
        createdAt: Timestamp.now(),
        lastEditedAt: Timestamp.now(),
      });
      AuditService.logPhiModification(
        noteData.providerId, 'provider', 'create', 'psychotherapy_note',
        docRef.id, noteData.patientId,
        'Created psychotherapy note (separate from clinical record per 45 CFR 164.501)'
      );
      return docRef.id;
    } catch (error) {
      console.error('Error creating psychotherapy note:', error);
      throw error;
    }
  }

  /**
   * Update a draft psychotherapy note.
   * IMPORTANT: Cannot update a signed note — immutable after signing.
   */
  static async updateNote(
    noteId: string,
    content: string,
    providerId: string
  ): Promise<void> {
    try {
      const existing = await this.getNote(noteId);
      if (!existing) throw new Error('Note not found');
      if (existing.status === 'signed') {
        throw new Error('Cannot edit a signed psychotherapy note. Signed notes are immutable per HIPAA.');
      }

      await updateDoc(doc(db, this.COLLECTION, noteId), {
        content,
        lastEditedAt: Timestamp.now(),
      });
      AuditService.logPhiModification(
        providerId, 'provider', 'update', 'psychotherapy_note',
        noteId, existing.patientId, 'Updated psychotherapy note draft'
      );
    } catch (error) {
      console.error('Error updating psychotherapy note:', error);
      throw error;
    }
  }

  /**
   * Sign and finalize a psychotherapy note (makes it immutable).
   */
  static async signNote(noteId: string, signedBy: string): Promise<void> {
    try {
      const existing = await this.getNote(noteId);
      if (existing?.status === 'signed') {
        throw new Error('Note is already signed.');
      }
      await updateDoc(doc(db, this.COLLECTION, noteId), {
        status: 'signed',
        signedBy,
        signedAt: Timestamp.now(),
        lastEditedAt: Timestamp.now(),
      });
      AuditService.logPhiModification(
        signedBy, 'provider', 'update', 'psychotherapy_note',
        noteId, existing?.patientId || '',
        'Psychotherapy note signed and finalized (immutable)'
      );
    } catch (error) {
      console.error('Error signing psychotherapy note:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time psychotherapy note updates for a patient.
   */
  static subscribeToPatientNotes(
    providerId: string,
    patientId: string,
    callback: (notes: PsychotherapyNote[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('providerId', '==', providerId),
      where('patientId', '==', patientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.fromFirestore(d.id, d.data())));
    });
  }

  /** Read all psychotherapy notes for a patient across all providers (admin/director use). */
  static subscribeToAllPatientNotes(
    patientId: string,
    callback: (notes: PsychotherapyNote[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('patientId', '==', patientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.fromFirestore(d.id, d.data())));
    });
  }

  private static fromFirestore(id: string, data: any): PsychotherapyNote {
    return {
      id,
      sessionId: data.sessionId || '',
      patientId: data.patientId,
      providerId: data.providerId,
      content: data.content || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastEditedAt: data.lastEditedAt?.toDate() || new Date(),
      signedBy: data.signedBy,
      signedAt: data.signedAt?.toDate(),
      status: data.status || 'draft',
      linkedClinicalNoteId: data.linkedClinicalNoteId,
    };
  }
}
